import {
  Line,
  XAxis,
  YAxis,
  LineChart,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';

import styled from '@emotion/styled';
import useCompete from '@stores/compete';
import { emptyObject } from '@constants/empty';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import useRatingInfo from '@cont/Compete/useRatingInfo';
import { getDateMonthString } from '@utils/dates';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import Image from '@base/FutureImage';
import {
  AXIS_STYLE,
  getFormatedData,
  getPastEpochList,
  getTextWidth,
  X_AXIS_STYLE
} from './utils';
import useRatingProgressInfo from '../useRatingProgressInfo';

const Content = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const colors = ['#E3B94B', '#F46A25', '#E653AD', '#8B82FF', '#209FB1'];
const reversedColors = [...colors].reverse();

const getColorByLevel = (value, userLevels) => {
  for (let i = 0; i < userLevels.length; i += 1) {
    if (value <= userLevels[i].thresholdValue) {
      return colors[i];
    }
  }

  return colors[colors.length - 1];
};

const SHADE_HEIGHT = 0.75;
const TOOL_TIP_HEIGHT = 20;
const CHART_EXTENSTION_LEN = 20;
const Y_AXIS_TICKS = [0, 1000, 2000, 4000, 7500, 10000];

const StyledSVGText = styled.text`
  fill: var(--color-i-white);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  line-height: ${Spacings.SPACING_5B};
  letter-spacing: 0;
  text-align: center;
  user-select: none;
`;

const ChartBlocker = styled.div`
  height: 88px;
  width: 110px;
  position: absolute;
  left: 45%;
  top: 30%;
  transform: translate(-45%, -30%);
  background-color: var(--color-base-1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 12px;
`;

const StyledP3 = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: 8px;
  text-align: center;
`;

const RatingChart = ({ chartHeight = 158 }) => {
  const lineRef = useRef();
  const gridRef = useRef();
  const yTicksRef = useRef();
  const renderRef = useRef(false);
  const [curPoints, setCurPoints] = useState([]);
  const [yTicksCoords, setYTicksCoords] = useState([]);
  const [chartDimension, setChartDimension] = useState({});
  const { levels } = useRatingInfo();
  const { data } = useRatingProgressInfo();
  const { selectedDuration } = useCompete();
  const { graph } = data;

  useEffect(() => {
    setYTicksCoords([]);
    setChartDimension({});
    setCurPoints([]);
    yTicksRef.current = false;
    renderRef.current = false;
  }, [data]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lineRef.current && gridRef.current && !renderRef.current) {
      const { curPoints: chartCurPoints } = lineRef.current.state;
      const { top, width, height } =
        gridRef.current?.props?.offset || emptyObject;
      if (chartCurPoints && top && width && height) {
        setCurPoints(chartCurPoints);
        setChartDimension({ top, width, height });
        renderRef.current = true;
      }
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!yTicksRef.current) {
      const rechartContainer = document.querySelector('.recharts-surface');
      const yAxis = rechartContainer?.querySelector(
        'g.recharts-layer.recharts-cartesian-axis.recharts-yAxis.yAxis'
      );
      const ticksGroup = yAxis?.querySelector(
        'g.recharts-cartesian-axis-ticks'
      );
      const yTicksCoordinates = [];
      if (ticksGroup?.children) {
        [...ticksGroup.children].forEach((child) => {
          const line = child.querySelector('line');
          yTicksCoordinates.push(line.y1.baseVal.value);
        });
      }
      if (yTicksCoordinates.length === Y_AXIS_TICKS.length) {
        yTicksCoordinates.reverse();
        setYTicksCoords(yTicksCoordinates);
        yTicksRef.current = true;
      }
    }
  });

  const isNoPointsToRender = useMemo(() => {
    return curPoints.filter((pointObj) => !!pointObj.value).length === 0;
  }, [curPoints]);

  const svgShadesAndBars = useMemo(() => {
    const svgElementsList = [];
    for (let i = 0; i < reversedColors.length; i += 1) {
      const yCoord = yTicksCoords[i];
      const color = reversedColors[i];
      const nextYCoord = yTicksCoords[i + 1];
      const rectHeight = nextYCoord - yCoord - SHADE_HEIGHT;

      const elements = [];
      if (!isNoPointsToRender) {
        elements.push(
          <rect
            opacity="0.05"
            width={chartDimension.width + CHART_EXTENSTION_LEN}
            y={yCoord}
            x="2"
            height={rectHeight}
            fill={color}
          />
        );
      }
      elements.push(
        <rect
          x={chartDimension.width + CHART_EXTENSTION_LEN}
          width="3"
          y={yCoord}
          height={rectHeight}
          fill={color}
        />,
        <rect
          x={chartDimension.width + 1}
          width={CHART_EXTENSTION_LEN + 2}
          y={yCoord - SHADE_HEIGHT}
          height={1}
          fill="var(--color-divider)"
        />
      );
      svgElementsList.push(...elements);
    }
    svgElementsList.push(
      <rect
        x={chartDimension.width + 1}
        width={CHART_EXTENSTION_LEN + 2}
        y={yTicksCoords[5] - SHADE_HEIGHT}
        height={1}
        fill="var(--color-divider)"
      />
    );
    return svgElementsList;
  }, [chartDimension.width, yTicksCoords, isNoPointsToRender]);

  const chartSVGLine = useMemo(() => {
    const svgElementsList = [];
    const pointsList = curPoints.filter((pointObj) => !!pointObj.value);

    for (let i = 0; i < pointsList.length - 1; i += 1) {
      const currentPoint = pointsList[i];
      const nextPoint = pointsList[i + 1];
      const isLast = i === pointsList.length - 2;
      const color = getColorByLevel(
        isLast ? nextPoint.value : currentPoint.value,
        levels
      );
      svgElementsList.push(
        <line
          x1={currentPoint.x}
          y1={currentPoint.y}
          x2={nextPoint.x}
          y2={nextPoint.y}
          style={{ stroke: color, strokeWidth: 1.5 }}
        />
      );
    }
    return svgElementsList;
  }, [curPoints, levels]);

  const tooltip = useMemo(() => {
    const pointsList = curPoints.filter((pointObj) => !!pointObj.value);
    const lastPoint = pointsList[pointsList.length - 1] || emptyObject;
    const textWidth = getTextWidth(lastPoint.value);

    if (!textWidth) {
      return null;
    }

    const toolTipColor = getColorByLevel(lastPoint.value, levels);
    const flipDirection = lastPoint.y < TOOL_TIP_HEIGHT;

    return (
      <g transform={`translate(${lastPoint.x},${lastPoint.y})`}>
        <rect
          x={`-${(textWidth + 6) / 2}`}
          y={flipDirection ? '14' : '-21'}
          width={textWidth + 6}
          height="12"
          fill={toolTipColor}
          rx={3}
          ry={3}
        />
        <StyledSVGText
          x={`-${textWidth / 2}`}
          y={flipDirection ? '23.25' : '-11.75'}
        >
          {lastPoint.value}
        </StyledSVGText>
        <rect />
        <g
          transform={
            flipDirection
              ? 'translate(3.5, 14.1) rotate(180)'
              : 'translate(-3.5, -9.1)'
          }
        >
          <path
            d="M0.700818 0.263382C0.50058 0.114193 0.256396 0.0229688 0 0H7C6.74359 0.0229688 6.49944 0.114193 6.29917 0.263382L4.3295 1.73087C3.84786 2.08971 3.15213 2.08971 2.6705 1.73087L0.700818 0.263382Z"
            fill={toolTipColor}
          />
        </g>
      </g>
    );
  }, [curPoints, levels]);

  const renderDot = useCallback(
    (props) => {
      const { cx, cy, index, value } = props;
      const isLastPoint = graph?.length - 1 === index;
      const dotFillColor = getColorByLevel(value, levels);

      if (!value) {
        return null;
      }

      if (isLastPoint) {
        return (
          <svg
            x={cx - 4}
            y={cy - 4}
            width={9}
            height={9}
            fill="red"
            viewBox="0 0 18 18"
          >
            <circle
              cx="9"
              cy="9"
              r="8"
              fill={dotFillColor}
              stroke="var(--color-base-1)"
              strokeWidth={2}
            />
          </svg>
        );
      }

      return (
        <svg x={cx - 2.5} y={cy - 2.5} width={5} height={5} viewBox="0 0 10 10">
          <circle
            cx="5"
            cy="5"
            r="4"
            fill={dotFillColor}
            stroke="var(--color-base-1)"
            strokeWidth={1}
          />
        </svg>
      );
    },
    [graph?.length, levels]
  );

  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const epochList = useMemo(() => {
    const epochParams = [];
    if (selectedDuration === 7) {
      epochParams.push(0, 7, 1);
    } else if (selectedDuration === 30) {
      epochParams.push(0, 30, 5);
    } else if (selectedDuration === 180) {
      epochParams.push(0, 180, 30);
    }
    return getPastEpochList(...epochParams).reverse();
  }, [selectedDuration]);

  return (
    <Content>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart
          data={getFormatedData(graph)}
          margin={{ top: 3, left: 2, right: 0, bottom: 0 }}
        >
          <CartesianGrid
            ref={gridRef}
            stroke="var(--color-divider)"
            strokeWidth={1}
          />
          <XAxis
            ticks={epochList}
            dataKey="timeline"
            domain={[epochList[0], epochList[epochList.length - 1]]}
            tickLine={false}
            scale="time"
            type="number"
            style={X_AXIS_STYLE}
            interval="preserveEnd"
            tick={{ dy: 5, dx: 5 }}
            stroke="var(--color-text-secondary)"
            strokeWidth={0}
            tickFormatter={(tick) => getDateMonthString(tick, false, false)}
          />
          <YAxis
            orientation="right"
            ticks={Y_AXIS_TICKS}
            style={AXIS_STYLE}
            tickMargin={25}
            interval={0}
            stroke="var(--color-text-secondary)"
            strokeWidth={0}
          />
          <svg
            width="100%"
            height="100%"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {svgShadesAndBars}
            {chartSVGLine}
            {tooltip}
          </svg>
          <Line
            type="linear"
            dataKey="rating"
            stroke="url(#gradient)"
            strokeWidth={2}
            isAnimationActive={false}
            dot={renderDot}
            ref={lineRef}
          />
        </LineChart>
      </ResponsiveContainer>
      {isNoPointsToRender && (
        <ChartBlocker>
          <Image
            src={
              isDarkModeOn
                ? 'compete/question-mark-dark.svg'
                : 'compete/question-mark-light.svg'
            }
            width={24}
            height={24}
            alt="question mark"
          />
          <StyledP3 variant="p3">Compete now to unlock your rating</StyledP3>
        </ChartBlocker>
      )}
    </Content>
  );
};

export default RatingChart;
